import tagularEvent from '../../utils/tagularEvent';
import { createElementClickedPayload } from '../../utils/createEventingPayloadsDE';
import { uuid } from '../../shared/utils';

type CompareAnalyticsPayload = {
  /** School slug emitted as a `schoolSlug` `customDimensions` entry (e.g. `university-of-north-carolina`). */
  schoolSlug: string;
  /** 1-based card position on the results page; serialized to `webElement.position`. */
  position?: number;
  /** Results-page view correlation id, threaded onto the top-level `viewCorrelationId`. */
  viewCorrelationId?: string;
  /** Per-product click correlation id, threaded onto the top-level `correlationId`. */
  correlationId?: string;
};

/**
 * Read the Cohesion `webContext` (page / userAgent / anonymousId /
 * sessionId / instanceId) off the global the Cohesion snippet
 * populates. Mirrors the access pattern the lead-submission and
 * conversion-creation paths already use (`ResultsPage`, `contactAPI`,
 * `buildConversionCreationRequest`). Returns `undefined` server-side or
 * before Cohesion has initialized so the beam degrades gracefully
 * instead of attaching an empty block.
 */
function getCohesionWebContext(): Record<string, unknown> | undefined {
  if (typeof window === 'undefined') return undefined;
  return window._Cohesion?.webContext as Record<string, unknown> | undefined;
}

/**
 * Stable `webElement.elementType` for the compare checkbox. Distinct
 * from the generic `'checkbox'` so analytics owners can isolate compare
 * interactions without inspecting `webElement.name`.
 */
export const COMPARE_ELEMENT_TYPE = 'dexp-compare-checkbox';

/**
 * Canonical `webElement.name` for the compare toggle (spec §7.4.3). The
 * program identity travels in a `schoolSlug` `customDimensions` entry
 * rather than in `name` so the beam stays self-describing.
 */
export const COMPARE_NAME = 'dexp-compare-toggle';

/**
 * `webElement.location` for compare beams. Pinned to the results page
 * surface where the compare control lives.
 */
export const COMPARE_LOCATION = 'dexp-results-page';

/** `webElement.text` label for the compare control. */
export const COMPARE_TEXT = 'Compare';

/**
 * Emit a compare toggle as a schema-valid `ElementClicked` beam.
 *
 * Routes through the canonical {@link createElementClickedPayload}
 * builder so the wire payload matches every other DE `ElementClicked`
 * (top-level `correlationId` / `viewCorrelationId`, a `webElement` block
 * with `position` nested inside, optional `customDimensions`) and so
 * Tagular auto-attaches `webContext` at beam time. The `ElementClicked`
 * schema has no `action` / `programId` / `schoolId` fields, so add vs
 * remove is encoded entirely in `webElement.htmlId` (`'enabled'` once
 * toggled on, `'disabled'` once toggled off) and the program is
 * identified by a `schoolSlug` `customDimensions` entry while `name`
 * stays the canonical `dexp-compare-toggle` (spec §7.4.3). `dslConfigId`,
 * when present (DSL result page), rides through as a second
 * `customDimensions` entry; the imperative path omits it.
 *
 * `correlationId` falls back to a freshly minted uuid so the beam is
 * never emitted with an undefined correlation, and `webContext` is
 * attached from the Cohesion global so the payload carries page /
 * session context.
 */
function emitCompare(
  payload: CompareAnalyticsPayload,
  checked: boolean,
  dslConfigId?: string,
): void {
  const { name, payload: elementClicked } = createElementClickedPayload({
    correlationId: payload.correlationId ?? uuid(),
    viewCorrelationId: payload.viewCorrelationId,
    elementType: COMPARE_ELEMENT_TYPE,
    name: COMPARE_NAME,
    location: COMPARE_LOCATION,
    position: payload.position,
    htmlId: checked ? 'enabled' : 'disabled',
    text: COMPARE_TEXT,
    customDimensions: [
      { key: 'schoolSlug', value: payload.schoolSlug },
      ...(dslConfigId ? [{ key: 'dslConfigId', value: dslConfigId }] : []),
    ],
  });
  const webContext = getCohesionWebContext();
  tagularEvent(
    name,
    webContext ? { ...elementClicked, webContext } : elementClicked,
  );
}

/**
 * Fires the compare `ElementClicked` beam for the select case — the user
 * just toggled the Compare checkbox on for a program
 * (`webElement.htmlId: 'enabled'`).
 */
export function emitCompareAdded(
  payload: CompareAnalyticsPayload,
  dslConfigId?: string,
): void {
  emitCompare(payload, true, dslConfigId);
}

/**
 * Mirror of {@link emitCompareAdded} for the deselect case
 * (`webElement.htmlId: 'disabled'`).
 */
export function emitCompareRemoved(
  payload: CompareAnalyticsPayload,
  dslConfigId?: string,
): void {
  emitCompare(payload, false, dslConfigId);
}
